export type AppRole = 'admin' | 'manager' | 'sales_executive' | 'relationship_manager';

/**
 * Maps each role to the sidebar/route keys it may access.
 * Use '*' to mean "all routes".
 *
 * Route keys must match the `key` field in buildSidebarOptions
 * and the routeKey prop passed to <RequireAccess>.
 */
export const ROLE_ROUTE_ACCESS: Record<AppRole, string[] | '*'> = {
  admin: '*',
  manager: [
    'dashboard',
    'kanban',
    'users',
    'leads',
    'employees',
    'user-plans',
    'plans',
    'settings',
    'notifications',
  ],
  sales_executive: ['dashboard', 'kanban', 'users', 'leads', 'settings', 'notifications'],
  relationship_manager: ['dashboard', 'kanban', 'users', 'leads', 'settings', 'notifications'],
};

const MANAGER_PERMISSIONS = [
  'dashboard:view_stats',
  'user_mgmt:read',
  'user_mgmt:create',
  'user_mgmt:update',
  'user_mgmt:delete',
  'user_mgmt:edit',
  'user_mgmt:assign_plan',
  'user_mgmt:create_profile',
  'user_mgmt:export',
  'user_mgmt:view_tasks_tab',
  'lead_mgmt:read',
  'lead_mgmt:create',
  'lead_mgmt:edit',
  'lead_mgmt:delete',
  'lead_mgmt:import',
  'lead_mgmt:status_change',
  'lead_mgmt:follow_up',
  'employee:view',
  'employee:create',
  'employee:edit',
  'employee:delete',
  'task_mgmt:create',
  'task_mgmt:update',
  'kanban:manage_pipeline',
  'kanban:view_all_assignees',
  'invoice_mgmt:read',
  'invoice_mgmt:download',
  'invoice_mgmt:send',
  'plan_mgmt:manage',
];

const SALES_EXECUTIVE_PERMISSIONS = [
  'user_mgmt:read',
  'user_mgmt:create_profile',
  'user_mgmt:edit',
  'lead_mgmt:read',
  'lead_mgmt:create',
  'lead_mgmt:edit',
  'lead_mgmt:status_change',
  'task_mgmt:create',
  'task_mgmt:update',
  'kanban:manage_pipeline',
  'kanban:view_all_assignees',
];

const RELATIONSHIP_MANAGER_PERMISSIONS = [
  'user_mgmt:read',
  'user_mgmt:create',
  'user_mgmt:update',
  'user_mgmt:delete',
  'user_mgmt:edit',
  'user_mgmt:create_profile',
  'user_mgmt:view_tasks_tab',
  'lead_mgmt:read',
  'lead_mgmt:status_change',
  'lead_mgmt:follow_up',
  'task_mgmt:create',
  'task_mgmt:update',
];

/**
 * Feature-level permissions (actions within a section).
 * Use '*' to mean "all permissions".
 */
export const ROLE_FEATURE_PERMISSIONS: Record<AppRole, string[] | '*'> = {
  admin: '*',
  manager: MANAGER_PERMISSIONS,
  sales_executive: SALES_EXECUTIVE_PERMISSIONS,
  relationship_manager: RELATIONSHIP_MANAGER_PERMISSIONS,
};

/**
 * Normalises a raw role string from the API to a known AppRole.
 * Handles capitalisation differences (e.g. "Admin" → "admin") and returns
 * null for anything unrecognised.
 */
function normaliseRole(role: string | null): AppRole | null {
  if (!role) return null;
  const lower = role.toLowerCase() as AppRole;
  if (
    lower === 'admin' ||
    lower === 'manager' ||
    lower === 'sales_executive' ||
    lower === 'relationship_manager'
  ) {
    return lower;
  }
  return null;
}

/**
 * Returns true when the given role may navigate to a route identified by routeKey.
 * Falls back to full access (admin behaviour) for any unrecognised role so the
 * sidebar is never accidentally left empty.
 */
export function canAccessRoute(role: string | null, routeKey: string): boolean {
  const normalisedRole = normaliseRole(role);
  if (!normalisedRole) return true;
  const allowed = ROLE_ROUTE_ACCESS[normalisedRole];
  if (allowed === '*') return true;
  return allowed.includes(routeKey);
}

/**
 * Returns true when the given role has a specific feature-level permission.
 * Falls back to full access for any unrecognised role.
 */
export function hasFeaturePermission(role: string | null, permission: string): boolean {
  const normalisedRole = normaliseRole(role);
  if (!normalisedRole) return true;
  const perms = ROLE_FEATURE_PERMISSIONS[normalisedRole];
  if (perms === '*') return true;
  return perms.includes(permission);
}

// --- Dashboard ---

export const canViewDashboardStats = (role: string | null): boolean =>
  hasFeaturePermission(role, 'dashboard:view_stats');

// --- Client profiles ---

export const canEditClientProfile = (role: string | null): boolean =>
  hasFeaturePermission(role, 'user_mgmt:edit');

export const canAssignClientPlan = (role: string | null): boolean =>
  hasFeaturePermission(role, 'user_mgmt:assign_plan');

export const canCreateClientProfile = (role: string | null): boolean =>
  hasFeaturePermission(role, 'user_mgmt:create_profile');

export const canExportClientProfiles = (role: string | null): boolean =>
  hasFeaturePermission(role, 'user_mgmt:export');

export const canViewClientTasksTab = (role: string | null): boolean =>
  hasFeaturePermission(role, 'user_mgmt:view_tasks_tab');

// --- Leads ---

export const canCreateLead = (role: string | null): boolean =>
  hasFeaturePermission(role, 'lead_mgmt:create');

export const canEditLead = (role: string | null): boolean =>
  hasFeaturePermission(role, 'lead_mgmt:edit');

export const canDeleteLead = (role: string | null): boolean =>
  hasFeaturePermission(role, 'lead_mgmt:delete');

export const canImportLeads = (role: string | null): boolean =>
  hasFeaturePermission(role, 'lead_mgmt:import');

export const canFollowUpLead = (role: string | null): boolean =>
  hasFeaturePermission(role, 'lead_mgmt:follow_up');

export const canChangeLeadStatus = (role: string | null): boolean =>
  hasFeaturePermission(role, 'lead_mgmt:status_change');

// --- Kanban ---

export const canManageKanbanPipeline = (role: string | null): boolean =>
  hasFeaturePermission(role, 'kanban:manage_pipeline');

export const canViewAllKanbanAssignees = (role: string | null): boolean =>
  hasFeaturePermission(role, 'kanban:view_all_assignees');

export const isKanbanAssignedOnly = (role: string | null): boolean => {
  const normalisedRole = normaliseRole(role);
  return normalisedRole === 'relationship_manager';
};

// --- Invoices ---

export const canDownloadInvoice = (role: string | null): boolean =>
  hasFeaturePermission(role, 'invoice_mgmt:download');

export const canSendInvoice = (role: string | null): boolean =>
  hasFeaturePermission(role, 'invoice_mgmt:send');

// --- Plans ---

export const canManagePlans = (role: string | null): boolean =>
  hasFeaturePermission(role, 'plan_mgmt:manage');
